/**
 * AdminClosingCostManager Component
 *
 * Manages Encompass Itemization Fees - view-only display with sync functionality.
 * Shows fees organized by LE section with search/filter capabilities.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  RefreshCw,
  Search,
  Database,
  CheckCircle2,
  XCircle,
  Clock,
  FileText,
  Shield
} from 'lucide-react';
import { PermissionGate } from '@/components/PermissionGate';
import { encompassFeeService } from '@/services/encompassFeeService';
import {
  EncompassItemizationFee,
  FeeSyncLog,
  FeeStatusResponse,
  LESection
} from '@/types/encompass-fees';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { createComponentLogger } from '@/utils/logger';
import { formatDistanceToNow, format } from 'date-fns';

const log = createComponentLogger('AdminClosingCostManager', 'fees');

type AdminClosingCostManagerProps = Record<string, never>;

// Section tabs including "All"
const SECTION_TABS: { key: LESection | 'all'; label: string }[] = [
  { key: 'all', label: 'All Fees' },
  { key: 'Section800', label: '800 - Origination' },
  { key: 'Section900', label: '900 - Services' },
  { key: 'Section1000', label: '1000 - Escrow' },
  { key: 'Section1100', label: '1100 - Title' },
  { key: 'Section1200', label: '1200 - Government' },
  { key: 'Section1300', label: '1300 - Other' }
];

// Stat Card Component
interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'danger';
  loading?: boolean;
  description?: string;
}

function StatCard({ title, value, icon, variant = 'default', loading, description }: StatCardProps) {
  const variantStyles = {
    default: 'bg-card',
    success: 'bg-green-50 border-green-200',
    warning: 'bg-amber-50 border-amber-200',
    danger: 'bg-red-50 border-red-200'
  };

  const iconStyles = {
    default: 'text-muted-foreground bg-muted/50',
    success: 'text-green-600 bg-green-100',
    warning: 'text-amber-600 bg-amber-100',
    danger: 'text-red-600 bg-red-100'
  };

  const valueStyles = {
    default: 'text-foreground',
    success: 'text-green-700',
    warning: 'text-amber-700',
    danger: 'text-red-700'
  };

  return (
    <Card className={cn('border', variantStyles[variant])}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {title}
            </p>
            {loading ? (
              <Skeleton className="h-8 w-20 mt-1" />
            ) : (
              <p className={cn('text-2xl font-bold mt-1', valueStyles[variant])}>
                {typeof value === 'number' ? value.toLocaleString() : value}
              </p>
            )}
            {description && !loading && (
              <p className="text-xs text-muted-foreground mt-1">{description}</p>
            )}
          </div>
          <div className={cn('p-2 rounded-full', iconStyles[variant])}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Sync Status Badge
function SyncStatusBadge({ status }: { status: FeeSyncLog['status'] }) {
  const config = {
    started: { icon: Clock, label: 'In Progress', variant: 'warning' as const },
    completed: { icon: CheckCircle2, label: 'Completed', variant: 'success' as const },
    failed: { icon: XCircle, label: 'Failed', variant: 'danger' as const }
  };

  const { icon: Icon, label, variant } = config[status];

  const variantClasses = {
    success: 'bg-green-100 text-green-800',
    warning: 'bg-amber-100 text-amber-800',
    danger: 'bg-red-100 text-red-800'
  };

  return (
    <Badge className={cn('gap-1', variantClasses[variant])}>
      <Icon className="h-3 w-3" />
      {label}
    </Badge>
  );
}

// Section Badge
function SectionBadge({ section }: { section: LESection }) {
  const sectionColors: Record<LESection, string> = {
    Section800: 'bg-blue-100 text-blue-800',
    Section900: 'bg-purple-100 text-purple-800',
    Section1000: 'bg-green-100 text-green-800',
    Section1100: 'bg-amber-100 text-amber-800',
    Section1200: 'bg-red-100 text-red-800',
    Section1300: 'bg-slate-100 text-slate-800'
  };

  return (
    <Badge variant="outline" className={cn('text-xs', sectionColors[section])}>
      {section.replace('Section', '')}
    </Badge>
  );
}

export const AdminClosingCostManager: React.FC<AdminClosingCostManagerProps> = () => {
  // State
  const [fees, setFees] = useState<EncompassItemizationFee[]>([]);
  const [status, setStatus] = useState<FeeStatusResponse | null>(null);
  const [syncHistory, setSyncHistory] = useState<FeeSyncLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState<LESection | 'all'>('all');

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [feesData, statusData, historyData] = await Promise.all([
        encompassFeeService.getFees(),
        encompassFeeService.getFeeStatus(),
        encompassFeeService.getSyncHistory(5)
      ]);
      setFees(feesData);
      setStatus(statusData);
      setSyncHistory(historyData);
      log.info('Data loaded', { feeCount: feesData.length });
    } catch (error) {
      log.error('Failed to load data', { error });
      toast.error('Failed to load fee data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle sync
  const handleSync = useCallback(async () => {
    try {
      setSyncing(true);
      log.info('Starting fee sync');
      const result = await encompassFeeService.triggerSync();
      toast.success(
        `Sync completed: ${result.feesAdded} added, ${result.feesUpdated} updated, ${result.feesRemoved} removed`
      );
      await fetchData();
    } catch (error) {
      log.error('Sync failed', { error });
      toast.error(error instanceof Error ? error.message : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  }, [fetchData]);

  // Filter fees
  const filteredFees = useMemo(() => {
    let result = fees;

    // Filter by section
    if (activeSection !== 'all') {
      result = result.filter(fee => fee.sections.includes(activeSection));
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        fee =>
          fee.name.toLowerCase().includes(query) ||
          fee.encompassFeeId.toLowerCase().includes(query) ||
          (fee.ucdFee && fee.ucdFee.toLowerCase().includes(query)) ||
          (fee.complianceFee && fee.complianceFee.toLowerCase().includes(query))
      );
    }

    return result;
  }, [fees, activeSection, searchQuery]);

  // Section counts
  const sectionCounts = useMemo(() => {
    const counts: Record<LESection | 'all', number> = {
      all: fees.length,
      Section800: 0,
      Section900: 0,
      Section1000: 0,
      Section1100: 0,
      Section1200: 0,
      Section1300: 0
    };

    for (const fee of fees) {
      for (const section of fee.sections) {
        if (section in counts) {
          counts[section as LESection]++;
        }
      }
    }

    return counts;
  }, [fees]);

  return (
    <PermissionGate permissions={['manage_closing_costs']}>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Encompass Fee Management</h1>
            <p className="text-muted-foreground">
              View and sync itemization fees from Encompass LOS
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-blue-50 text-blue-700">
              <Shield className="w-3 h-3 mr-1" />
              Admin Access
            </Badge>
            <Button onClick={handleSync} disabled={syncing || loading}>
              <RefreshCw className={cn('w-4 h-4 mr-2', syncing && 'animate-spin')} />
              {syncing ? 'Syncing...' : 'Sync from Encompass'}
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard
            title="Total Fees"
            value={status?.totalFees || 0}
            icon={<Database className="h-5 w-5" />}
            loading={loading}
          />
          <StatCard
            title="Active Fees"
            value={status?.activeFees || 0}
            icon={<FileText className="h-5 w-5" />}
            variant="success"
            loading={loading}
          />
          <StatCard
            title="Last Sync"
            value={
              status?.lastSync
                ? formatDistanceToNow(new Date(status.lastSync.startedAt), { addSuffix: true })
                : 'Never'
            }
            icon={<RefreshCw className="h-5 w-5" />}
            loading={loading}
            description={
              status?.lastSync
                ? format(new Date(status.lastSync.startedAt), 'MMM d, yyyy h:mm a')
                : undefined
            }
          />
          <StatCard
            title="Sync Status"
            value={status?.lastSync?.status ? status.lastSync.status.charAt(0).toUpperCase() + status.lastSync.status.slice(1) : 'N/A'}
            icon={
              status?.lastSync?.status === 'completed' ? (
                <CheckCircle2 className="h-5 w-5" />
              ) : status?.lastSync?.status === 'failed' ? (
                <XCircle className="h-5 w-5" />
              ) : (
                <Clock className="h-5 w-5" />
              )
            }
            variant={
              status?.lastSync?.status === 'completed'
                ? 'success'
                : status?.lastSync?.status === 'failed'
                ? 'danger'
                : 'default'
            }
            loading={loading}
          />
        </div>

        {/* Section Tabs */}
        <div className="border-b">
          <div className="flex gap-1 -mb-px overflow-x-auto">
            {SECTION_TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveSection(tab.key)}
                className={cn(
                  'px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors',
                  activeSection === tab.key
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30'
                )}
              >
                {tab.label}
                <Badge variant="secondary" className="ml-2 text-xs">
                  {sectionCounts[tab.key]}
                </Badge>
              </button>
            ))}
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search fees by name, ID, or UCD code..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="text-sm text-muted-foreground">
            Showing {filteredFees.length} of {fees.length} fees
          </div>
        </div>

        {/* Fee Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 space-y-4">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[300px]">Fee Name</TableHead>
                    <TableHead className="w-[100px]">Fee ID</TableHead>
                    <TableHead className="w-[150px]">Sections</TableHead>
                    <TableHead className="w-[150px]">UCD Fee</TableHead>
                    <TableHead className="w-[200px]">Compliance Fee</TableHead>
                    <TableHead className="w-[80px] text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredFees.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        {searchQuery ? 'No fees match your search' : 'No fees available'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredFees.map(fee => (
                      <TableRow key={fee.id} className="hover:bg-muted/50">
                        <TableCell className="font-medium">{fee.name}</TableCell>
                        <TableCell className="font-mono text-sm text-muted-foreground">
                          {fee.encompassFeeId}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {fee.sections.map(section => (
                              <SectionBadge key={section} section={section} />
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {fee.ucdFee || '-'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {fee.complianceFee || '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          {fee.isActive ? (
                            <Badge variant="outline" className="bg-green-50 text-green-700">
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-slate-100 text-slate-600">
                              Inactive
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Sync History */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Sync History</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : syncHistory.length === 0 ? (
              <p className="text-center py-4 text-muted-foreground">No sync history available</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Added</TableHead>
                    <TableHead className="text-right">Updated</TableHead>
                    <TableHead className="text-right">Removed</TableHead>
                    <TableHead>Duration</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {syncHistory.map(sync => (
                    <TableRow key={sync.id}>
                      <TableCell className="font-medium">
                        {format(new Date(sync.startedAt), 'MMM d, yyyy h:mm a')}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {sync.syncType}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <SyncStatusBadge status={sync.status} />
                      </TableCell>
                      <TableCell className="text-right text-green-600 font-medium">
                        +{sync.feesAdded}
                      </TableCell>
                      <TableCell className="text-right text-blue-600 font-medium">
                        {sync.feesUpdated}
                      </TableCell>
                      <TableCell className="text-right text-red-600 font-medium">
                        -{sync.feesRemoved}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {sync.completedAt
                          ? `${Math.round(
                              (new Date(sync.completedAt).getTime() -
                                new Date(sync.startedAt).getTime()) /
                                1000
                            )}s`
                          : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </PermissionGate>
  );
};

export default AdminClosingCostManager;
